<template>
  <div class="reports-history-table-wrapper">
    <div
      v-if="loading && !tableData.length"
      class="reports-history-table-empty-wrapper"
    >
      <gl-loader progress-spinner />
    </div>
    <div
      v-if="!loading && !tableData.length"
      class="reports-history-table-empty-wrapper"
    >
      <div class="reports-history-table-empty-text-wrapper">
        You currently have no reports
      </div>
    </div>

    <DataTable
      v-show="tableData.length"
      :always-show-paginator="false"
      class="p-table-groupped p-table-nowrap-data p-table-pale-border"
      :lazy="true"
      :loading="loading"
      paginator
      responsive-layout="scroll"
      :row-class="rowClass"
      :rows="perPage"
      :rows-per-page-options="pagesOptions"
      scroll-height="flex"
      :scrollable="true"
      :total-records="totalRecords"
      :value="tableData"
      @page="onPage($event)"
      @row-click="onRowClick"
      @sort="onSort($event)"
    >
      <Column
        field="address"
        header="Address"
        :styles="{ width: '30%' }"
      >
        <template #body="slotProps">
          <div class="reports-history-table__address-wrapper">
            <div
              v-if="isTableLocked && !slotProps.data.isLegacy"
              class="reports-history-table__lock-icon"
            >
              <gl-icon
                gray
                :height="24"
                name="I-Lock-Fill"
                :width="24"
              />
            </div>
            
            <GlBlockchainIcon
              v-else
              :blockchain-icon="slotProps.data.coin"
            />
           
            <div class="ellipsis bold">
              {{
                trancateString(slotProps.data.address, isMobile ? 4 : 12) || '–'
              }}
            </div>

            <gl-icon
              :height="20"
              name="copy-gray"
              :width="20"
              @click.stop="copyAddress(slotProps.data.address)"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="isExample"
        field="includes"
        header="Plan Example"
        style="width: 50%"
      >
        <template #body="slotProps">
          <GlStatusChip
            v-if="slotProps.data.planLabel"
            type="brand"
          >
            <template #icon>
              <gl-icon
                brand
                :height="20"
                name="I-S-Upgrade Plan"
                :width="20"
              />
            </template>
            {{ slotProps.data.planLabel }}
          </GlStatusChip>
        </template>
      </Column>
      <Column
        v-else
        field="includes"
        style="width: 50%"
      >
        <template #body="slotProps">
          <GlStatusPopover
            offset="0"
            :open="openedPopoverIndex === slotProps.index"
            :placement="isMobile ? 'bottom' : 'right'"
            width="264px"
            z-index="1"
            @change="onStatusPopoverChange"
          >
            <GlStatusChip
              v-if="slotProps.data.isLegacy"
              type="warning"
              @click.native.stop="
                togglePopover(
                  openedPopoverIndex !== slotProps.index,
                  slotProps,
                )
              "
            >
              <template #icon>
                <gl-icon
                  :height="20"
                  name="I-S-Info"
                  warning
                  :width="20"
                />
              </template>
              Legacy Report
            </GlStatusChip>

            <template #header>
              Legacy Report
            </template>

            <template #body>
              Updates for this report are no longer supported. Create a new
              report to access the most recent data.
            </template>

            <template #action>
              <GlButton
                brand
                full
                title="Generate Report"
                @click="generateReport(slotProps)"
              />
            </template>
          </GlStatusPopover>
        </template>
      </Column>
      <Column
        field="createdAt"
        header="Date & Time"
        :sortable="!isExample"
        :styles="{ width: '130px' }"
      >
        <template #body="slotProps">
          <div
            class="reports-history-table__date"
            v-html="formatTimestampDate(slotProps.data.createdAt)"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
// Components
import GlIcon from '@/components/gl-icon'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import GlLoader from '@/components/gl-loader'
import GlBlockchainIcon from '@/components/gl-blockchain-icon'
import GlStatusChip from '@/components/badges/gl-status-chip'
import GlStatusPopover from '@/components/popovers/gl-status-popover'
import GlButton from '@/components/gl-button'
// Utils
import { formatDate } from '@/utils/format-date'
import { trancateString } from '@/utils/text-formatter'
import { copyText } from '@/utils/copy-text'
import { scrollToSelector } from '@/utils/scroll-to'
//mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

export default {
  name: 'ReportHistoryTable',
  components: {
    GlIcon,
    DataTable,
    Column,
    GlLoader,
    GlBlockchainIcon,
    GlStatusChip,
    GlStatusPopover,
    GlButton,
  },
  mixins: [deviceWidthMixin],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    lazyParams: {
      type: Object,
      default: () => ({
        pageSize: 5,
        pageNumber: 1,
        sort: {},
      }),
    },
    perPage: {
      type: Number,
      default: 10,
    },
    totalRecords: {
      type: Number,
      default: 10,
    },
    isExample: {
      type: Boolean,
      default: false,
    },
    isTableLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pagesOptions: [5, 10, 20, 50, 100],
      openedPopoverIndex: null,
    }
  },
  computed: {
    headerHeight() {
      return getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height',
      )
    },
  },
  methods: {
    trancateString,
    formatDate,
    copyText,
    scrollToSelector,
    scrollToSelectorItem(wrap, aim) {
      this.scrollToSelector(wrap, aim, parseInt(this.headerHeight))
    },
    rowClass(data) {
      return this.isTableLocked && !data.isLegacy ? 'cursor-not-allowed' : 'pointer'
    },
    onPage(event) {
      this.$emit('on-page', event)
    },
    onSort(event) {
      this.$emit('on-sort', event)
    },
    formatTimestampDate(val) {
      return val
        ? this.formatDate(new Date(val), 'dd.MM.yyyy HH:mm', true)
        : '–'
    },
    onRowClick(event) {
      if (this.isTableLocked && !event.data.isLegacy) return
      this.goToReport({ eventData: event.data })
    },
    onStatusItemClick(item, event) {
      this.goToReport({ eventData: event.data, anchor: item.key })
    },
    goToReport({ eventData, anchor = undefined }) {
      if (anchor == 'full') anchor = undefined
      this.$router.push({
        name: 'report',
        query: {
          address: eventData.address,
          type: eventData.coin,
          exampleId: eventData.exampleId,
          ...(eventData.isLegacy ? { isLegacy: eventData.isLegacy } : {}),
          anchor,
        },
      })
    },
    copyAddress(address) {
      this.copyText(address).then(() => {
        this.$toast.add({
          severity: 'info',
          summary: 'Success Message',
          detail: 'Copied!',
          life: 3000,
        })
      })
    },
    togglePopover(isOpen, slotProps) {
      if (this.isTableLocked && !slotProps.data.isLegacy) return
      this.openedPopoverIndex = isOpen ? slotProps.index : null
    },
    onStatusPopoverChange(event) {
      if (!event) {
        this.openedPopoverIndex = null
      }
    },
    generateReport(event) {
      this.goToReport({ eventData: { ...event.data, isLegacy: false } })
      this.openedPopoverIndex = null
    },
  },
}
</script>

<style>
.reports-history-table__address-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reports-history-table__address-wrapper .pp-blockchain-icon svg {
  width: 40px;
  height: 40px;
}

.reports-history-table__lock-icon {
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 100%;
  border: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
  background: var(--Basic-200, rgba(9, 21, 64, 0.08));
}

.reports-history-table-wrapper {
  height: 100%;
}

/* table */

.reports-history-table-wrapper .p-datatable-wrapper {
  border: 1px solid var(--Basic-100, rgba(9, 21, 64, 0.04));
  border-radius: 16px;
}

.reports-history-table-wrapper .p-datatable .p-datatable-thead > tr > th {
  padding: 16px 24px;
  font-weight: 600;
  color: var(--Basic-700, rgba(9, 21, 64, 0.56));
  border-bottom: 1px solid var(--Basic-100, rgba(9, 21, 64, 0.04));
}

.reports-history-table-wrapper .p-datatable .p-datatable-tbody > tr > td {
  padding: 15px 24px;
  border-bottom: 1px solid var(--Basic-100, rgba(9, 21, 64, 0.04));
}

.p-datatable .p-paginator-bottom {
  border: 1px solid var(--Basic-100, rgba(9, 21, 64, 0.04));
  border-radius: 16px;
}

.reports-history-table-empty-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  height: 100%;
}

.reports-history-table-empty-text-wrapper {
  max-width: 440px;
  font-weight: 500;
  text-align: center;
}

.reports-history-table-wrapper
  .p-datatable-responsive-scroll
  > .p-datatable-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* full scroll height */
.reports-history-table-wrapper .p-datatable-flex-scrollable {
  display: block;
}
.p-datatable-flex-scrollable .p-datatable-wrapper {
  height: auto;
}

.reports-history-table__date {
  min-width: max-content;
  font-size: 14px;
  font-weight: 500;
}
.reports-history-table__date .pp-date__time {
  margin-top: 4px;
  color: var(--Basic-700, rgba(9, 21, 64, 0.56));
  font-size: var(--Size-S, 12px);
  font-weight: 500;
  line-height: var(--Line-Height-S, 16px);
}
</style>
